import { traduzirCampo, deveIgnorarCampo, aplicarMapeamentoEspecial } from './traducoes';

/**
 * Unidades de medida para campos específicos
 */
const unidadesMedida: Record<string, string> = {
  'Peso': 'kg',
  'Altura': 'cm',
  'Circunferência Abdominal': 'cm',
  'Perímetro Encefálico': 'cm',
  'Temperatura': 'ºC',
  'Frequência Respiratória': 'rpm',
  'Frequência Cardíaca': 'bpm',
  'Glicemia Capilar': 'mg/dL'
};

/**
 * Verifica se um campo deve ter unidade de medida
 */
function obterUnidadeMedida(campo: string): string {
  return unidadesMedida[campo] || '';
}

/**
 * Função para formatar uma data completa no formato DD/MM/YYYY
 */
export function formatarData(dataObj: any): string {
  if (!dataObj || typeof dataObj !== 'object') return '';
  
  // Dia e mês com padding de zero à esquerda
  const dia = dataObj.$D?.toString().padStart(2, '0') || '';
  const mes = ((dataObj.$M ?? -1) + 1).toString().padStart(2, '0') || '';
  const ano = dataObj.$y || '';
  
  if (!dia || !mes || !ano) return '';
  return `${dia}/${mes}/${ano}`;
}

/**
 * Função auxiliar para formatar problemas
 */
function formatarProblemas(problems: any[]): string[] {
  let idx = 1;
  return problems
    .map((item: any) => {
      if (item && typeof item === 'object') {
        if (item.display && item.code) {
          return `${idx++} - Problema > ${item.display} - ${item.code}`;
        } else if (item.label) {
          return `${idx++} - Problema > ${item.label}`;
        }
      } else if (typeof item === 'string' && item.trim() !== '') {
        return `${idx++} - Problema > ${item}`;
      }
      return null;
    })
    .filter(Boolean) as string[];
}

/**
 * Função auxiliar para formatar condutas
 */
function formatarCondutas(condutas: any[]): string[] {
  let idx = 1;
  return condutas
    .map((item: any) => {
      if (item && typeof item === 'object') {
        if (item.label) {
          return `${idx++} - Conduta > ${item.label}`;
        } else if (item.display) {
          return `${idx++} - Conduta > ${item.display}`;
        }
      } else if (typeof item === 'string' && item.trim() !== '') {
        return `${idx++} - Conduta > ${item}`;
      }
      return null;
    })
    .filter(Boolean) as string[];
}

/**
 * Converte um objeto complexo de dados em texto formatado
 */
export function formatarCamposComoTexto(obj: any, prefixo = '', contexto = ''): string {
  let linhas: string[] = [];
  
  // Verificar se o objeto é uma data
  if (obj && obj.$isDayjsObject) {
    return `${prefixo}: ${formatarData(obj)}`;
  }
  
  // Se for o objeto raiz e houver problems ou New_Conduta, exibe-os e retorna
  if (prefixo === '') {
    if (Array.isArray(obj.problems) && obj.problems.length > 0) {
      linhas.push(...formatarProblemas(obj.problems));
    }
    if (Array.isArray(obj.New_Conduta) && obj.New_Conduta.length > 0) {
      linhas.push(...formatarCondutas(obj.New_Conduta));
    }
    // Remove para não processar novamente
    delete obj['problems'];
    delete obj['New_Conduta'];
  }
  
  // Garantir que as chaves sejam processadas em uma ordem específica
  const chaves = Object.keys(obj);
  
  // Ordenar as chaves para garantir uma ordem consistente
  // Colocar chaves importantes como 'Peso', 'Altura', etc. primeiro
  const chavesOrdenadas = [...chaves].sort((a, b) => {
    // Lista de campos em ordem de prioridade para os Dados Objetivos
    const ordemDadosObjetivos = [
      'Peso', 'bodyWeight', 'weight',
      'Altura', 'bodyHeight', 'height',
      'Circunferência Abdominal', 'abdominalCircumference',
      'Perímetro Encefálico', 'headCircumference',
      'pressure',
      'Temperatura', 'temperature',
      'Frequência Respiratória', 'respiratoryRate',
      'Frequência Cardíaca', 'cardiacRate',
      'Glicemia Capilar', 'glicemiaCapilar',
      'Dor', 'pain',
      'Consumo diário de álcool (ml)', 'alcoholic',
      'Tabagismo', 'smoking'
    ];

    // Se estamos lidando com Dados Objetivos
    if (prefixo.includes('Dados Objetivos')) {
      const indexA = ordemDadosObjetivos.indexOf(a);
      const indexB = ordemDadosObjetivos.indexOf(b);
      
      // Se ambos estão na lista, use a ordem definida
      if (indexA >= 0 && indexB >= 0) {
        return indexA - indexB;
      }
      // Se apenas um está na lista, priorize-o
      if (indexA >= 0) return -1;
      if (indexB >= 0) return 1;
    }
    
    // Ordem alfabética para o resto
    return a.localeCompare(b);
  });
  
  // Processar as chaves na ordem determinada
  for (const chave of chavesOrdenadas) {
    if (!Object.hasOwn(obj, chave)) continue;
    
    // Ignorar campos específicos
    if (deveIgnorarCampo(chave)) continue;
    if (chave === 'problems' || chave === 'Problema principal') continue; // Ignorar problems e Problema principal
    if (["CarePlans", "CondutasPassadas", "ProblemasPassados"].includes(chave)) continue; // Ignorar blocos duplicados
    
    const valor = obj[chave];
    
    // Ignorar valores nulos ou indefinidos
    if (valor === null || valor === undefined) continue;
    
    let chaveFormatada = traduzirCampo(chave);
    // Tradução especial para New_Problema
    if (chave === 'New_Problema') chaveFormatada = 'Problema';
    
    // Aplicar mapeamento especial se for uma chave top-level
    if (!prefixo) {
      chaveFormatada = aplicarMapeamentoEspecial(chaveFormatada);
    }
    
    const nomeCampo = prefixo ? `${prefixo} > ${chaveFormatada}` : chaveFormatada;
    
    // Se for um objeto com $isDayjsObject, tratar como data
    if (typeof valor === 'object' && valor.$isDayjsObject) {
      linhas.push(`${nomeCampo}: ${formatarData(valor)}`);
      continue;
    }
    
    if (typeof valor === 'object') {
      if (Array.isArray(valor)) {
        // Filtrar itens não nulos
        const itensValidos = valor.filter(v => v !== null && v !== undefined);
        
        // Processar arrays
        itensValidos.forEach((item: any, idx: number) => {
          const numeroIndice = idx + 1; // Índice começando em 1
          
          // Determinar o nome singular baseado no contexto
          let nomeSingular = chaveFormatada;
          if (chaveFormatada === 'Exames') nomeSingular = 'Exame';
          else if (chaveFormatada === 'Plano de tratamento') nomeSingular = 'Plano';
          else if (chaveFormatada === 'Conduta') nomeSingular = 'Conduta';
          else if (chaveFormatada === 'Problema') nomeSingular = 'Problema'; // Para New_Problema
          
          if (typeof item === 'object' && item !== null) {
            // Para arrays de objetos
            if (chaveFormatada === 'Plano de tratamento') {
              // Processamento especial para planos de tratamento
              const planoTexto = item.plano;
              const prazoValor = item.prazo;
              const prazoTexto = prazoValor ? traduzirCampo(prazoValor.toString()) || prazoValor : '';
              
              // Formatar plano sem mostrar o prazo como campo separado
              if (planoTexto) {
                linhas.push(`${numeroIndice} - ${nomeSingular}: ${planoTexto} - ${prazoTexto}`);
              }
            } else {
              // Para outros arrays de objetos (não planos)
              linhas.push(formatarCamposComoTexto(item, `${numeroIndice} - ${nomeSingular}`, chaveFormatada));
            }
          } else {
            // Para arrays de valores simples
            const itemFormatado = typeof item === 'string' ? traduzirCampo(item) : item;
            linhas.push(`${numeroIndice} - ${nomeSingular}: ${itemFormatado}`);
          }
        });
      } else {
        // Se o valor for um objeto não-array e tiver uma propriedade 'value'
        if (valor.value !== undefined) {
          // Para o caso especial de Tabagismo
          if (nomeCampo.includes('Tabagismo')) {
            const valorFormatado = traduzirCampo(valor.value);
            linhas.push(`${nomeCampo} > Valor: ${valorFormatado}`);
          } else {
            const valorFormatado = typeof valor.value === 'string' ? traduzirCampo(valor.value) : valor.value;
            linhas.push(`${nomeCampo} > Valor: ${valorFormatado}`);
            
            // Se o valor for 'alterado', adiciona também a propriedade 'alterado'
            if (valor.value === 'alterado' && valor.alterado !== undefined) {
              linhas.push(`${nomeCampo} > Alterado: ${valor.alterado}`);
            }
          }
        } else {
          // Para outros objetos, processa recursivamente
          const linhasFilho = formatarCamposComoTexto(valor, nomeCampo, chaveFormatada);
          if (linhasFilho) {
            linhas.push(linhasFilho);
          }
        }
      }
    } else {
      // Para valores simples
      const valorFormatado = typeof valor === 'string' ? traduzirCampo(valor) : valor;
      
      // Caso especial para o campo de consumo de álcool
      if (chaveFormatada === 'Consumo diário de álcool (ml)' || chave === 'alcoholic') {
        // Verificar se o valor parece ser um consumo de álcool válido
        const valorNumerico = Number(valorFormatado);
        if (!isNaN(valorNumerico) || valorFormatado === 0 || valorFormatado === '0') {
          linhas.push(`${nomeCampo}: ${valorFormatado}`);
        }
      } 
      // Verificar se é um dado objetivo que deve ter unidade de medida
      else if (prefixo && prefixo.includes('Dados Objetivos')) {
        const unidade = obterUnidadeMedida(chaveFormatada);
        if (unidade) {
          linhas.push(`${nomeCampo}: ${valorFormatado} ${unidade}`);
        } else {
          linhas.push(`${nomeCampo}: ${valorFormatado}`);
        }
      } else {
        // Exibir problemas apenas com índices sequenciais para itens válidos
        if (Array.isArray(obj.problems) && obj.problems.length > 0 && prefixo === '') {
          let problemaIdx = 1;
          obj.problems.forEach((item: any) => {
            let textoProblema = '';
            if (item && typeof item === 'object') {
              if (item.display && item.code) {
                textoProblema = `${problemaIdx} - Problema > ${item.display} - ${item.code}`;
              } else if (item.label) {
                textoProblema = `${problemaIdx} - Problema > ${item.label}`;
              }
            } else if (typeof item === 'string' && item.trim() !== '') {
              textoProblema = `${problemaIdx} - Problema > ${item}`;
            }
            if (textoProblema) {
              linhas.push(textoProblema);
              problemaIdx++;
            }
          });
          // Exibir condutas no mesmo padrão, se houver
          if (Array.isArray(obj.New_Conduta) && obj.New_Conduta.length > 0) {
            let condutaIdx = 1;
            obj.New_Conduta.forEach((item: any) => {
              let textoConduta = '';
              if (item && typeof item === 'object') {
                if (item.label) {
                  textoConduta = `${condutaIdx} - Conduta > ${item.label}`;
                } else if (item.display) {
                  textoConduta = `${condutaIdx} - Conduta > ${item.display}`;
                }
              } else if (typeof item === 'string' && item.trim() !== '') {
                textoConduta = `${condutaIdx} - Conduta > ${item}`;
              }
              if (textoConduta) {
                linhas.push(textoConduta);
                condutaIdx++;
              }
            });
          }
          // Não processa mais nada relacionado a problema ou conduta
          return linhas.join('\n');
        }
        linhas.push(`${nomeCampo}: ${valorFormatado}`);
      }
    }
  }
  
  // Remover duplicatas de problemas (caso haja)
  if (prefixo === '' && linhas.length > 0) {
    const problemasSet = new Set();
    linhas = linhas.filter(linha => {
      if (linha.startsWith('Problema:')) {
        if (problemasSet.has(linha)) return false;
        problemasSet.add(linha);
      }
      // Remover qualquer linha 'Problema principal:'
      if (linha.startsWith('Problema principal:')) return false;
      return true;
    });
  }
  
  // Remover linhas de Exames Físicos com 'Valor: Não avaliado' ou 'Valor: not_evaluated'
  linhas = linhas.filter(linha => {
    if (linha.includes('Exames Físicos') && (linha.includes('Valor: Não avaliado') || linha.includes('Valor: not_evaluated'))) {
      return false;
    }
    return true;
  });
  
  return linhas.filter(Boolean).join('\n');
}

/**
 * Processa dados antes de formatá-los para texto
 * - Converte datas
 * - Trata casos especiais
 */
export function processarDadosParaFormatacao(data: any): any {
  // Clonar os dados para não modificar o original
  const dadosProcessados = { ...data };
  
  // Processar datas
  let dataReferenciaTratada = false;
  
  if (dadosProcessados['Data de Referencia'] || dadosProcessados['Data de Referência']) {
    const dataRef = dadosProcessados['Data de Referencia'] || dadosProcessados['Data de Referência'];
    if (dataRef && typeof dataRef === 'object' && dataRef.$isDayjsObject) {
      dadosProcessados['Data de Referência'] = formatarData(dataRef);
      delete dadosProcessados['Data de Referencia']; // Remover versão sem acento
      dataReferenciaTratada = true;
    }
  }
  
  // Processar arrays de exames e planos
  ['newExames', 'newPlan', 'New_Conduta'].forEach(arrayKey => {
    if (dadosProcessados[arrayKey] && Array.isArray(dadosProcessados[arrayKey])) {
      dadosProcessados[arrayKey].forEach((item: any) => {
        // Verificar se o item existe antes de tentar acessar suas propriedades
        if (item && item.date && typeof item.date === 'object' && item.date.$isDayjsObject) {
          item.data = formatarData(item.date);
          delete item.date;
        }
      });
    }
  });
  
  // Evitar duplicação de data de referência
  // Se a data de referência já foi tratada e existe nos exames, remover dos exames
  if (dataReferenciaTratada && dadosProcessados.newExames) {
    dadosProcessados.newExames.forEach((exame: any) => {
      if (exame.date === dadosProcessados['Data de Referência']) {
        delete exame.date;
      }
    });
  }
  
  return dadosProcessados;
}

// Campos que devem ser ignorados no resultado final
const camposIgnoradosResultado = new Set(['Data de Referencia']);

/**
 * Função única que processa e formata os dados para texto, incluindo informações do paciente
 */
export function formatarDadosParaTexto(
  data: any,
  infosPaciente?: {
    idade?: string;
    sexo?: string;
    escolaridade?: string;
  },
  tratamentos?: any[],
  condicoes?: any[],
  alergias?: any[],
  imunizacoes?: { description?: string; code?: string; narrative?: string }[]
): string {
  // Remover a data de referência dos dados originais
  const dadosClonados = { ...data };
  delete dadosClonados['Data de Referencia'];
  delete dadosClonados['Data de Referência'];
  
  // Processar os dados sem a data de referência
  const dadosProcessados = processarDadosParaFormatacao(dadosClonados);
  
  // Obter o texto formatado sem a data de referência
  const textoFormatado = formatarCamposComoTexto(dadosProcessados);
  
  // Caso alguma referência à data ainda tenha escapado, filtramos as linhas
  let linhas = textoFormatado.split('\n').filter(linha => 
    !linha.includes('Data de Referência') && !linha.includes('Data de Referencia')
  );

  // Separar blocos principais
  const infosPacienteLinhas: string[] = [];
  if (infosPaciente) {
    if (infosPaciente.idade) {
      infosPacienteLinhas.push(`Idade: ${infosPaciente.idade}`);
    }
    if (infosPaciente.sexo) {
      infosPacienteLinhas.push(`Sexo de nascimento: ${infosPaciente.sexo}`);
    }
    if (infosPaciente.escolaridade) {
      infosPacienteLinhas.push(`Grau de escolaridade: ${infosPaciente.escolaridade}`);
    }
  }

  // Função para inserir uma linha em branco entre blocos
  function addBloco(arr: string[], bloco: string[] | string) {
    if (Array.isArray(bloco)) {
      if (bloco.length > 0) {
        if (arr.length > 0 && arr[arr.length - 1] !== '') arr.push('');
        arr.push(...bloco);
      }
    } else if (bloco && bloco.trim() !== '') {
      if (arr.length > 0 && arr[arr.length - 1] !== '') arr.push('');
      arr.push(bloco);
    }
  }

  // Separar blocos de anamnese, dados objetivos, exames físicos, problemas, condutas
  const anamneseBloco = linhas.filter(l => l.startsWith('Anamnese'));
  const dadosObjetivosBloco = linhas.filter(l => l.startsWith('Dados Objetivos'));
  const examesFisicosBloco = linhas.filter(l => l.startsWith('Exames Físicos'));
  const problemasBloco = linhas.filter(l => l.match(/^\d+ - Problema/));
  const condutasBloco = linhas.filter(l => l.match(/^\d+ - Conduta/));
  // Restante das linhas (que não são dos blocos acima)
  const outrosBlocos = linhas.filter(l =>
    !l.startsWith('Anamnese') &&
    !l.startsWith('Dados Objetivos') &&
    !l.startsWith('Exames Físicos') &&
    !l.match(/^\d+ - Problema/) &&
    !l.match(/^\d+ - Conduta/)
  );

  let resultado: string[] = [];
  addBloco(resultado, infosPacienteLinhas);
  addBloco(resultado, anamneseBloco);
  addBloco(resultado, dadosObjetivosBloco);
  addBloco(resultado, examesFisicosBloco);
  addBloco(resultado, problemasBloco);
  addBloco(resultado, condutasBloco);
  addBloco(resultado, outrosBlocos);

  // Adicionar tratamentos
  if (tratamentos && tratamentos.length > 0) {
    resultado.push('');
    resultado.push('Tratamentos:');
    tratamentos.forEach((t: any, idx: number) => {
      resultado.push(`${idx + 1}. Descrição: ${t.description || '-'} | Posologia: ${t.dose || '-'} | Observação: ${t.note || '-'}`);
    });
  }

  // Adicionar condições
  if (condicoes && condicoes.length > 0) {
    resultado.push('');
    resultado.push('Condições:');
    condicoes.forEach((c: any, idx: number) => {
      resultado.push(`${idx + 1}. Condição: ${c.description || '-'}${c.cid10 ? ` (${c.cid10})` : ''} | Status clínico: ${c.clinicalStatus || '-'} | Status de verificação: ${c.verificationStatus || '-'} | Observação: ${c.description || '-'}`);
    });
  }

  // Adicionar alergias
  if (alergias && alergias.length > 0) {
    resultado.push('');
    resultado.push('Alergias:');
    alergias.forEach((a: any, idx: number) => {
      resultado.push(`${idx + 1}. Substância: ${a.description || '-'} | Status clínico: ${a.clinicalStatus || '-'} | Status de verificação: ${a.verificationStatus || '-'} | Categoria: ${a.category || '-'} | Criticidade: ${a.criticality || '-'}`);
    });
  }

  // Adicionar imunizações
  if (imunizacoes && imunizacoes.length > 0) {
    resultado.push('');
    resultado.push('Imunizações:');
    imunizacoes.forEach((imz, idx) => {
      resultado.push(`${idx + 1}. Vacina: ${imz.description || '-'} - ${imz.code || '-'}`);
      if (imz.narrative) {
        resultado.push(`Narrativa de vacinação: ${imz.narrative}`);
      }
    });
  }

  // Remover linhas em branco duplicadas
  return resultado.filter((l, i, arr) => l !== '' || arr[i - 1] !== '').join('\n');
}